import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { CustomerSummary, Ticket, TicketPriority, TicketStatus, TicketUser } from 'src/api/models';
import { AzureAuthService } from 'src/service/azure-auth.service';

@Component({
  selector: 'app-support-ticket-editor-modal',
  templateUrl: './support-ticket-editor-modal.component.html',
  styleUrls: ['./support-ticket-editor-modal.component.css']
})
export class SupportTicketEditorModalComponent implements OnChanges {
  @ViewChild('editTicket', { static: false }) editTicketModal: ModalDirective;

  @Input() selectedTicket: Ticket | null = null;
  @Input() priorityTypes: TicketPriority[] = [];
  @Input() statusTypes: TicketStatus[] = [];
  @Input() allCustomers: CustomerSummary[] = [];
  @Input() users: TicketUser[] = [];

  @Input() noteText = '';
  @Output() noteTextChange = new EventEmitter<string>();

  @Output() saveTicket = new EventEmitter<Ticket>();
  @Output() acknowledgeTicket = new EventEmitter<Ticket>();
  @Output() acceptOwnershipTicket = new EventEmitter<Ticket>();
  @Output() closeTicket = new EventEmitter<Ticket>();
  @Output() addNoteRequested = new EventEmitter<void>();
  @Output() customerSelectionChanged = new EventEmitter<number | null>();

  descriptionHtml = '';

  descriptionEditorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: '100%',
    minHeight: '250px',
    translate: 'no',
    sanitize: true,
    placeholder: 'Enter ticket description',
    toolbarHiddenButtons: [
      [
        'undo',
        'redo',
        'strikeThrough',
        'subscript',
        'superscript',
        'justifyLeft',
        'justifyCenter',
        'justifyRight',
        'justifyFull',
        'indent',
        'outdent',
        'insertOrderedList',
        'fontName'
      ],
      [
        'fontSize',
        'textColor',
        'backgroundColor',
        'customClasses',
        'link',
        'unlink',
        'insertImage',
        'insertVideo',
        'insertHorizontalRule',
        'removeFormat',
        'toggleEditorMode'
      ]
    ]
  };

  private turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced'
  });

  constructor(public azureAuthService: AzureAuthService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedTicket']) {
      this.syncDescriptionFromTicket();
    }
  }

  open(): void {
    this.syncDescriptionFromTicket();
    this.editTicketModal?.show();
  }

  hide(): void {
    this.editTicketModal?.hide();
  }

  onDescriptionEditorChange(html: string): void {
    this.descriptionHtml = html || '';
    if (!this.selectedTicket) {
      return;
    }

    this.selectedTicket.description = this.turndownService.turndown(this.descriptionHtml);
  }

  onNoteTextChanged(value: string): void {
    this.noteText = value;
    this.noteTextChange.emit(value);
  }

  onCustomerChanged(): void {
    if (!this.selectedTicket) {
      return;
    }

    this.selectedTicket.assignedToUserId = null;
    this.selectedTicket.assignedToUser = null;
    this.customerSelectionChanged.emit(this.selectedTicket.customerId ?? null);
  }

  onSave(): void {
    if (this.selectedTicket) {
      this.saveTicket.emit(this.selectedTicket);
    }
  }

  onAcknowledge(): void {
    if (this.selectedTicket) {
      this.acknowledgeTicket.emit(this.selectedTicket);
    }
  }

  onAcceptOwnership(): void {
    if (this.selectedTicket) {
      this.acceptOwnershipTicket.emit(this.selectedTicket);
    }
  }

  onCloseTicket(): void {
    if (this.selectedTicket) {
      this.closeTicket.emit(this.selectedTicket);
    }
  }

  onAddNote(): void {
    if (!this.selectedTicket || !this.noteText?.trim()) {
      return;
    }

    this.addNoteRequested.emit();
  }

  canAcknowledgeTicket(ticket: Ticket): boolean {
    if (!ticket || this.isTicketAcknowledged(ticket)) {
      return false;
    }

    if (this.azureAuthService.isAdmin()) {
      return true;
    }

    const currentUserId = this.azureAuthService.userProfile?.userId;
    if (currentUserId != null && ticket.assignedToUserId != null && currentUserId === ticket.assignedToUserId) {
      return true;
    }

    const currentName = this.azureAuthService.userProfile?.fullName;
    return !!currentName && !!ticket.assignedToUser && currentName === ticket.assignedToUser;
  }

  isTicketAcknowledged(ticket: Ticket): boolean {
    return !!ticket?.acknowledgedDate;
  }

  isTicketCompleted(ticket: Ticket): boolean {
    if (!ticket) {
      return false;
    }

    if (ticket.status?.id != null) {
      return ticket.status.id >= 4;
    }

    return !!ticket.resolvedDate || !!ticket.closedDate;
  }

  getFilteredUsers(): TicketUser[] {
    if (!this.users || !this.selectedTicket) {
      return this.users || [];
    }

    return this.users.filter(user =>
      user.customerName === this.selectedTicket.customerName ||
      (user.email && user.email.includes('@indicium-dynamics.com.au'))
    );
  }

  getSlaMetricCardClass(value?: number | null): string {
    if (value == null || Number.isNaN(value)) {
      return 'ticket-sla-dashboard__item--na';
    }

    if (value < 25) {
      return 'ticket-sla-dashboard__item--danger';
    }

    if (value < 50) {
      return 'ticket-sla-dashboard__item--warning';
    }

    return 'ticket-sla-dashboard__item--default';
  }

  getSlaLevelCardClass(level?: string | null): string {
    const normalized = (level || '').toLowerCase();
    if (normalized.includes('gold')) {
      return 'ticket-sla-dashboard__item--gold';
    }
    if (normalized.includes('silver')) {
      return 'ticket-sla-dashboard__item--silver';
    }
    if (normalized.includes('bronze')) {
      return 'ticket-sla-dashboard__item--bronze';
    }
    return 'ticket-sla-dashboard__item--na';
  }

  getSlaHoursDisplay(ticket: Ticket, metric: 'acknowledge' | 'complete'): string {
    const remaining = this.getSlaRemainingHours(ticket, metric);
    const total = this.getSlaTotalHours(ticket, metric);

    if (remaining == null || total == null) {
      return 'N/A';
    }

    return `${remaining.toFixed(1)}h / ${total.toFixed(1)}h`;
  }

  private syncDescriptionFromTicket(): void {
    this.descriptionHtml = this.convertMarkdownToHtml(this.selectedTicket?.description);
  }

  private convertMarkdownToHtml(markdown?: string | null): string {
    if (!markdown) {
      return '';
    }

    const rendered = marked.parse(markdown, { async: false });
    return typeof rendered === 'string' ? rendered : '';
  }

  private getSlaTotalHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    if (metric === 'acknowledge') {
      return ticket.acknowledgeSlaTotalHours ?? null;
    }

    return ticket.completeSlaTotalHours ?? ticket.resolveSlaThresholdHours ?? null;
  }

  private getSlaRemainingHours(ticket: Ticket, metric: 'acknowledge' | 'complete'): number | null {
    const source = ticket as Ticket & {
      acknowledgeSlaRemainingHours?: number;
      completeSlaRemainingHours?: number;
      AcknowledgeSlaRemainingHours?: number;
      CompleteSlaRemainingHours?: number;
    };

    const explicitRemaining = metric === 'acknowledge'
      ? (source.acknowledgeSlaRemainingHours ?? source.AcknowledgeSlaRemainingHours)
      : (source.completeSlaRemainingHours ?? source.CompleteSlaRemainingHours);

    if (explicitRemaining != null && !Number.isNaN(explicitRemaining)) {
      return explicitRemaining;
    }

    const percent = metric === 'acknowledge'
      ? ticket.acknowledgeSlaRemainingPercent
      : ticket.completeSlaRemainingPercent;
    const total = this.getSlaTotalHours(ticket, metric);
    if (percent == null || total == null || Number.isNaN(percent) || Number.isNaN(total)) {
      return null;
    }

    return (Math.max(0, Math.min(100, percent)) / 100) * total;
  }
}
